// services/endpoints/streaming.ts
import { useMutation, useQuery } from 'react-query'

import axiosInstance from 'api/axiosInstance'
import { type ITipPayload } from 'types/interfaces/ITypes'

export type EndStreamPayload = {
  channel_arn: string
  force?: boolean
}

export type IssueTokenRequest = {
  pass_code: string
  username?: string
  creator_id?: string
}

export const endStream = async (payload: EndStreamPayload) => {
  const { data } = await axiosInstance.post('/api/user/end-stream', payload)
  return data
}
export const deleteStreamArchives = async (streamIds: string[]) => {
  const { data } = await axiosInstance({
    method: 'DELETE',
    url: '/api/user/stream/archives',
    data: {
      stream_ids: streamIds
    }
  })

  return data
}
export type KickStreamUserPayload = {
  stream_id: string
  user_id?: string
  viewer_id?: string
}

export type KickStreamUserResponse = {
  success?: boolean
  message?: string
  kicked?: boolean
}

export const kickStreamUser = async (payload: KickStreamUserPayload) => {
  const { data } = await axiosInstance.post<KickStreamUserResponse>(
    '/api/user/stream/kick',
    payload
  )
  return data
}


export type LeaveStreamViewerPayload = {
  stream_id: string | string
  viewer_id?: string
}

export type LeaveStreamViewerResponse = {
  success: boolean
  updated?: boolean
  message?: string
  id?: string
  stream_id?: string
  user_id?: string
  left_at?: string
}

export const leaveStreamViewer = async (payload: LeaveStreamViewerPayload) => {
  const { data } = await axiosInstance.post<LeaveStreamViewerResponse>(
    '/api/user/stream/leave',
    payload
  )
  return data
}




export const sendTipToStream = async (payload: ITipPayload) => {
    const { stream_id, ...data } = payload

    const { data: response } = await axiosInstance({
        method: 'POST',
        url: `/api/user/stream/${stream_id}/tip`,
        data
    })

    return response
}

export type IssueTokenResponse = {
  // server can include this so you know who you are
  role?: 'viewer' | 'creator'
  playback_url?: string          // present for viewers'
  stream_id?: string
  viewer_count?: number
  expires_in?: number
  show_tips?: boolean
  goal?: {
    stream_id: string
    user_id: string
    goal_id: string
    amount_goal: number | null
    amount_tipped: number
    remaining: number | null
    percent: number | null
    public: boolean
    reward?: string | null
    completed: boolean
  } | null
  chat: {
    token: string
    roomArn: string
    sessionExpirationTime: string
    tokenExpirationTime: string
    region?: string
  }
}

// PROD/AUTH route (recommended single endpoint)
export const issueStreamAccess = async (payload: {
  username: string
  pass_code?: string   // required for viewers, omitted for creators
}) => {
  const { data } = await axiosInstance.post<IssueTokenResponse>(
    '/api/user/stream/access-token',
    payload
  )
  return data
}


export type ViewerItem = {
  id: number
  viewer_id: string | null
  user_id: string | null
  username: string | null
  name: string | null
  avatar: string | null
  tipped_amount: number
  joined_at: string
  left_at: string | null
}

export type GetViewersResponse = {
  success: boolean
  active: boolean
  stream_id: string | null
  count: number
  viewers: ViewerItem[]
  pagination: {
    current_page: number
    per_page: number
    last_page: number
    total: number
  }
}

export type GetViewersParams = {
  username?: string
  creator_id?: string
  page?: number
}

export const fetchViewers = async (params: GetViewersParams) => {
  const { data } = await axiosInstance.get<GetViewersResponse>(
    '/api/user/stream/viewers',
    { params }
  )

  return data
}

// CHAT HISTORY BELOW

export type ChatHistoryItem = {
  id?: string
  message_id?: string
  user_id?: string | string | null
  username?: string | null
  content: string
  attrs?: any
  sent_at?: string
  created_at?: string
  avatar_url?:string
}

export type ChatHistoryResponse = { items: ChatHistoryItem[] } | ChatHistoryItem[]

/**
 * DEV-ONLY: fetch persisted chat history for a room. Your backend should return either
 *  - { items: ChatHistoryItem[] } or
 *  - ChatHistoryItem[]
 */
export async function fetchChatHistory(params: {
  roomArn: string
  stream_id?: string | number | null
  limit?: number
  beforeId?: string
}) {
  const { data } = await axiosInstance.get<ChatHistoryResponse>(
    '/api/user/stream/history',
    {
      params: {
        roomArn: params.roomArn,
        stream_id: params.stream_id ?? null,
        limit: params.limit,
        beforeId: params.beforeId,
      },
    }
  )

  return Array.isArray(data) ? data : (data.items ?? [])
}

/**
 * DEV-ONLY: send a message to a local “tap” endpoint so you can persist chat without Lambda.
 * Backend route example: POST /api/user/internal/ivs-chat/dev-tap
 */
export async function devTapMessage(payload: {
  roomArn: string
  content: string
  messageId?: string | null
  userId?: string | number | null
  username?: string | null
  attrs?: any
  ts?: number
  avatar_url?: string
  stream_id?: string | null
  to_user?: string | number | null
}) {
  try {
    await axiosInstance.post('/api/user/internal/ivs-chat/dev-tap', payload)
  } catch (e) {
    // swallow
  }
}

// PRIVATE LIVE CHAT (NON-IVS)

export type SendPrivateLiveChatPayload = {
  stream_id?: string | number | null
  to_user: string | number
  content: string
}

export type SendPrivateLiveChatResponse = {
  item: {
    id: string | string
    message_id?: string
    stream_id?: string | number | null
    user_id?: string | string
    to_user?: number | string | null
    username?: string
    avatar_url?: string
    content: string
    ts?: number
    created_at?: string
  }
}

export const sendPrivateLiveChatMessage = async (
  payload: SendPrivateLiveChatPayload
) => {
  const { data } = await axiosInstance.post<SendPrivateLiveChatResponse>(
    '/api/user/live-chat/private-message',
    payload
  )

  return data
}

export type StreamGoalResponse = {
  stream_id: string
  user_id: string
  goal_id: string
  amount_goal: number | null
  amount_tipped: number
  remaining: number | null
  percent: number | null
  public: boolean
  reward?: string | null
  completed: boolean
}
export const getStreamGoal = async (streamId: string) => {
  const { data } = await axiosInstance.get<StreamGoalResponse>(`/api/user/stream/${streamId}/goal`)
  return data
}

// Creator-only (auth) — sets or updates the goal amount for a stream
export const setStreamGoal = async (
  streamId: string,
  amount: number,
  isPublic: boolean,
  reward?: string,
  goalId?: string | null
) => {
  const payload: {
    amount: number
    public: boolean
    reward?: string
    goal_id?: string
  } = {
    amount,
    public: isPublic,
  }

  if (goalId != null) {
    payload.goal_id = goalId
  }

  if (isPublic && reward?.trim()) {
    payload.reward = reward.trim()
  }

  const { data } = await axiosInstance.post<StreamGoalResponse>(
    `/api/user/stream/${streamId}/goal`,
    payload
  )

  return data
}

export interface CreatorChatAccessRequest {
  username: string
}

export interface CreatorChatAccessResponse {
  role: 'creator'
  chat: {
    token: string
    roomArn: string
    sessionExpirationTime: string
    tokenExpirationTime: string
    region: string
  }
}

export const issueCreatorChatAccess = async (
  payload: CreatorChatAccessRequest
): Promise<CreatorChatAccessResponse> => {
  const { data } = await axiosInstance.post<CreatorChatAccessResponse>(
    '/api/user/stream/creator-chat-access',
    payload
  )
  return data
}


// SAVE STREAMS

type SetSaveToVaultPayload = {
  streamId: string
  save_to_vault: boolean
}

const setSaveToVault = async ({
  streamId,
  save_to_vault,
}: SetSaveToVaultPayload) => {
  const { data } = await axiosInstance({
    method: 'POST',
    url: `/api/user/stream/${streamId}/save-to-vault`,
    data: {
      save_to_vault,
    },
  })

  return data
}

export function useSetSaveToVault() {
  return useMutation({
    mutationFn: setSaveToVault,
  })
}




// STREAM ARCHIVES

export type StreamArchiveItem = {
  stream_id: string | null
  channel_arn: string | null
  recording_status: string | null
  recording_started_at: string | null
  recording_ended_at: string | null
  duration_ms: number | null
  s3_prefix: string | null
  metadata_key: string | null
  playlist_key: string | null
  playback_url: string | null
  thumbnail_key: string | null
  thumbnail_url: string | null
  renditions: {
    key: string | null
    url: string | null
    resolution_width: number | null
    resolution_height: number | null
  }[]
}

export type GetStreamArchivesResponse = {
  success: boolean
  archives: StreamArchiveItem[]
  pagination: {
    current_page: number
    per_page: number
    total: number
    last_page: number
  }
}

export type GetStreamArchivesParams = {
  page?: number
  per_page?: number
}

export const fetchStreamArchives = async (
  params: GetStreamArchivesParams = {}
) => {
  const { data } = await axiosInstance.get<GetStreamArchivesResponse>(
    '/api/user/stream/archives',
    { params }
  )

  return data
}



/* =========================
   OBS settings
   ========================= */

export type ObsSettingsResponse = {
  obs_server: string | null
  obs_key: string | null
  obs_alternative_server: string | null
  obs_alternative_key: string | null
}

export const getObsSettings = async (): Promise<ObsSettingsResponse> => {
  const { data } = await axiosInstance.get('/api/user/stream/obs-settings')
  return data?.data ?? data
}

export const useObsSettings = () => {
  return useQuery(['obs-settings'], getObsSettings, {
    refetchOnWindowFocus: false,
  })
}

export type RegenerateObsKeyPayload = {
  type: 'primary' | 'alternative'
}

export const regenerateObsKey = async (
  payload: RegenerateObsKeyPayload
): Promise<ObsSettingsResponse> => {
  const { data } = await axiosInstance.post(
    '/api/user/stream/obs-settings/regenerate',
    payload
  )
  return data?.data ?? data
}

export const useRegenerateObsKey = () => {
  return useMutation(regenerateObsKey)
}



